import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { ExpressionEditor } from "../common/LazyExpressionEditor";
import { compileSections, parsePrompt } from "./promptImport";
import { useUnsavedGuard } from "./unsavedGuard";
import { DialogPanel } from "../shared/DialogPanel";
import { SavedNote, XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";
import { DateRangePicker } from "../shared/DateRangePicker";

import type { Field } from "./SelfService";
import { SYSTEM_MESSAGE_ID, uid } from "./builder/layout";

export interface Section { id: string; name: string; content: string; starts_at?: string | null; expires_at?: string | null; paused?: boolean; fields?: Field[]; block?: string | null; description?: string | null; portal_title?: string | null }

// Perfil por canal: colunas do agente de cada canal (espelha
// carmillion_core.channels.CHANNEL_FIELDS). Nada é compartilhado entre canais.
export type Channel = "whatsapp" | "instagram" | "messenger";
export const CHANNELS: { id: Channel; label: string; prompt: string; sections: string; followup: string }[] = [
  { id: "whatsapp", label: "WhatsApp", prompt: "prompt", sections: "prompt_sections", followup: "followup" },
  { id: "instagram", label: "Instagram", prompt: "instagram_prompt", sections: "instagram_prompt_sections", followup: "instagram_followup" },
  { id: "messenger", label: "Messenger", prompt: "messenger_prompt", sections: "messenger_prompt_sections", followup: "messenger_followup" },
];
export const channelOf = (c: Channel) => CHANNELS.find((x) => x.id === c)!;

// Sessão reservada: injetada como mensagem à parte a cada chamada (nunca entra
// no prompt compilado nem no histórico). Sempre presente, sempre no fim.
export const SYSTEM_MESSAGE_NAME = "Mensagem do Sistema";

export function normalizeSections(list: Section[]): Section[] {
  const rest = list.filter((s) => s.id !== SYSTEM_MESSAGE_ID);
  const reserved = list.find((s) => s.id === SYSTEM_MESSAGE_ID)
    ?? { id: SYSTEM_MESSAGE_ID, name: SYSTEM_MESSAGE_NAME, content: "" };
  return [...rest, { ...reserved, name: SYSTEM_MESSAGE_NAME }];
}

// Janela do bloco: entra às 00:00 do início, some no dia SEGUINTE ao fim. Aqui é só
// o visual — o filtro real que vai pra IA é no backend (compile_sections em runtime).
export function sectionWindow(s: { starts_at?: string | null; expires_at?: string | null }): "active" | "scheduled" | "expired" {
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }); // "YYYY-MM-DD"
  if (s.starts_at && today < s.starts_at) return "scheduled";
  if (s.expires_at && today > s.expires_at) return "expired";
  return "active";
}
interface HistRow { id: string; changed_by: string; created_at: string; added: number; removed: number }
interface DiffSeg { text: string; hl: boolean }
interface DiffLine { type: "ctx" | "add" | "del"; segments: DiffSeg[] }
interface SectionDiffT { title: string; added: number; removed: number; lines: DiffLine[] }
interface HistDetail extends HistRow { lines: DiffLine[]; sections?: SectionDiffT[] }

const ghostBtn: React.CSSProperties = { padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" };
const redBtn: React.CSSProperties = { padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" };
const iconBtn: React.CSSProperties = { background: "none", border: "none", color: "var(--ink-soft)", cursor: "pointer", padding: "2px 4px", lineHeight: 1, display: "flex", alignItems: "center" };

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
  } catch { return iso; }
}

function DiffView({ lines }: { lines: DiffLine[] }) {
  return (
    <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, lineHeight: 1.6, background: "var(--surface-raised)", borderRadius: 8, padding: "8px 0", overflowX: "auto" }}>
      {lines.map((ln, i) => {
        const isAdd = ln.type === "add", isDel = ln.type === "del";
        const bg = isAdd ? "var(--ok-soft)" : isDel ? "var(--danger-soft)" : "transparent";
        const sign = isAdd ? "+" : isDel ? "-" : " ";
        const signColor = isAdd ? "var(--ok)" : isDel ? "var(--danger)" : "var(--ink-muted)";
        const hlBg = isAdd ? "var(--ok-line)" : "var(--danger-line)";
        return (
          <div key={i} style={{ display: "flex", background: bg, padding: "0 10px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            <span style={{ width: 14, flexShrink: 0, color: signColor, userSelect: "none" }}>{sign}</span>
            <span style={{ flex: 1, color: "var(--ink)" }}>
              {ln.segments.map((s, j) => (<span key={j} style={s.hl ? { background: hlBg, borderRadius: 3 } : undefined}>{s.text}</span>))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DiffModal({ agentId, histId, channel, onClose }: { agentId: string; histId: string; channel: Channel; onClose: () => void }) {
  const { data } = useQuery<HistDetail>({
    queryKey: ["prompt-history", agentId, histId],
    queryFn: () => api.get(`/agents/${agentId}/prompt-history/${histId}?channel=${channel}`),
  });
  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onClose()} label="Alterações do prompt" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 20, width: 720, maxWidth: "92vw", maxHeight: "85vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Alterações do prompt</span>
          <button aria-label="Fechar" onClick={onClose} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        {data ? (
          <>
            <div style={{ fontSize: 12, color: "var(--ink-soft)", display: "flex", gap: 14 }}>
              <span>{data.changed_by}</span><span>{fmtDate(data.created_at)}</span>
              <span style={{ fontFamily: "var(--font-mono)" }}><span style={{ color: "var(--ok)" }}>+{data.added}</span>{" "}<span style={{ color: "var(--danger)" }}>-{data.removed}</span></span>
            </div>
            {data.sections && data.sections.length > 0 ? (
              data.sections.map((sec, k) => (
                <div key={k} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, color: "var(--brand)", fontWeight: 600, fontFamily: "var(--font-mono)" }}>
                    {sec.title.toUpperCase()}{" "}
                    <span style={{ color: "var(--ok)" }}>+{sec.added}</span>{" "}
                    <span style={{ color: "var(--danger)" }}>-{sec.removed}</span>
                  </div>
                  <DiffView lines={sec.lines} />
                </div>
              ))
            ) : (
              <DiffView lines={data.lines} />
            )}
          </>
        ) : <div style={{ fontSize: 13, color: "var(--ink-soft)", padding: 20 }}>Carregando…</div>}
      </DialogPanel>
    </div>
  );
}

// Liga/desliga o agente no canal — operacional: vale na hora, sem publicar.
// Desligado, a conversa segue registrada e o prompt salvo é preservado.
function ChannelToggle({ agent, channel }: { agent: any; channel: Channel }) {
  const qc = useQueryClient();
  const key = `${channel}_enabled`;
  const label = channelOf(channel).label;
  const [on, setOn] = useState<boolean>(!!agent[key]);
  const mut = useMutation({
    mutationFn: (v: boolean) => api.patch(`/agents/${agent.id}`, { [key]: v }),
    onSuccess: (_d, v) => { setOn(v); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
        <input type="checkbox" checked={on} disabled={mut.isPending} onChange={(e) => mut.mutate(e.target.checked)}
          style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
        Ativar o agente no {label}
      </label>
      <span style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.5 }}>
        {on
          ? `Mensagens do ${label} usam o prompt abaixo (o do WhatsApp não é aplicado). Liga/desliga vale na hora, sem publicar.`
          : `Desligado: as conversas do ${label} continuam registradas, mas o agente não responde. O prompt fica salvo.`}
      </span>
    </div>
  );
}

export function ConfigPrompt({ agent, channel = "whatsapp" }: { agent: any; channel?: Channel }) {
  const qc = useQueryClient();
  const ch = channelOf(channel);
  // Self-Service: sessões ganham campos editáveis (chips {{ campo.* }}) — só no WhatsApp.
  const isGlobal = agent.template_kind === "global" && channel === "whatsapp";
  const insertRef = useRef<((t: string) => void) | null>(null);
  const initial: Section[] = normalizeSections(
    agent[ch.sections]?.length ? agent[ch.sections] : [{ id: uid(), name: "Geral", content: agent[ch.prompt] ?? "" }]
  );
  const [sections, setSections] = useState<Section[]>(initial);
  const snap = (secs: Section[]) => JSON.stringify(secs);
  const [savedSnap, setSavedSnap] = useState(() => snap(initial));
  const [selectedId, setSelectedId] = useState<string>(initial[0]?.id ?? "");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedHist, setSelectedHist] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<HistRow | null>(null);
  const [restoreStep, setRestoreStep] = useState<1 | 2>(1);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRaw, setImportRaw] = useState("");
  const [importPreview, setImportPreview] = useState<Section[] | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "ok" | "err">("idle");

  function reorder(fromId: string, toId: string) {
    if (fromId === toId) return;
    if (fromId === SYSTEM_MESSAGE_ID || toId === SYSTEM_MESSAGE_ID) return;  // fixa no fim
    setSections((prev) => {
      const from = prev.findIndex((s) => s.id === fromId);
      const to = prev.findIndex((s) => s.id === toId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  const { data: history = [] } = useQuery<HistRow[]>({
    queryKey: ["prompt-history", agent.id, channel],
    queryFn: () => api.get(`/agents/${agent.id}/prompt-history?channel=${channel}`),
  });

  const save = useMutation({
    mutationFn: () => api.patch(`/agents/${agent.id}`, { [ch.sections]: sections }),
    onSuccess: () => {
      setSavedSnap(snap(sections));
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
      qc.invalidateQueries({ queryKey: ["prompt-history", agent.id] });
    },
  });

  useUnsavedGuard(snap(sections) !== savedSnap, save.mutateAsync);

  const restoreMut = useMutation({
    mutationFn: (histId: string) => api.post<Record<string, any>>(`/agents/${agent.id}/prompt-history/${histId}/restore`, {}),
    onSuccess: (a) => {
      const secs = normalizeSections(
        a[ch.sections]?.length ? a[ch.sections] : [{ id: uid(), name: "Geral", content: a[ch.prompt] ?? "" }]
      );
      setSections(secs);
      setSavedSnap(snap(secs));
      setSelectedId(secs[0]?.id ?? "");
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
      qc.invalidateQueries({ queryKey: ["prompt-history", agent.id] });
      setRestoring(null);
    },
  });

  const importMut = useMutation({
    mutationFn: (secs: Section[]) => api.patch(`/agents/${agent.id}`, { [ch.sections]: secs }),
    onSuccess: (_data, secs) => {
      setSections(secs);
      setSavedSnap(snap(secs));
      setSelectedId(secs[0]?.id ?? "");
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
      qc.invalidateQueries({ queryKey: ["prompt-history", agent.id] });
      setImportPreview(null);
    },
  });

  function startImport() {
    const parsed = parsePrompt(importRaw).map((s) => ({ id: uid(), ...s }));
    if (!parsed.length) return;
    setImportPreview(parsed);
    setImportOpen(false);
  }

  // Importar substitui as sessões normais, mas PRESERVA a Mensagem do Sistema atual.
  function confirmImport() {
    if (!importPreview) return;
    importMut.mutate(normalizeSections([
      ...importPreview,
      ...sections.filter((s) => s.id === SYSTEM_MESSAGE_ID),
    ]));
  }

  async function copyPrompt() {
    try {
      // A Mensagem do Sistema não faz parte do prompt compilado.
      await navigator.clipboard.writeText(compileSections(sections.filter((s) => s.id !== SYSTEM_MESSAGE_ID)));
      setCopyState("ok");
    } catch {
      setCopyState("err");
    }
    setTimeout(() => setCopyState("idle"), 1500);
  }

  const selected = sections.find((s) => s.id === selectedId) ?? sections[0];
  const sectionsListRef = useRef<HTMLDivElement>(null);

  function addSection() {
    const s: Section = { id: uid(), name: "", content: "" };
    // Entra antes da Mensagem do Sistema, que é sempre a última.
    setSections((prev) => {
      const next = [...prev];
      next.splice(Math.max(next.length - 1, 0), 0, s);
      return next;
    });
    setSelectedId(s.id);
    setRenamingId(s.id);
    // A nova sessão entra no fim da lista — rola até ela ficar visível.
    requestAnimationFrame(() => {
      const el = sectionsListRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
  function rename(id: string, name: string) {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  }
  function setContent(id: string, content: string) {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, content } : s)));
  }
  // Self-Service: campos editáveis são do global (aba Campos, agents.ss_fields); aqui só
  // se insere o chip (botão "Campos editáveis" do editor) e se pinta chave morta de vermelho.
  const campoFields: Field[] | undefined = isGlobal ? (agent.ss_fields ?? []) : undefined;
  const campoKeys = campoFields?.map((f) => f.key);
  function setWindow(id: string, starts_at: string | null, expires_at: string | null) {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, starts_at, expires_at } : s)));
  }
  function togglePaused(id: string) {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, paused: !s.paused } : s)));
  }
  function remove(id: string) {
    setSections((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (id === selectedId) setSelectedId(next[0]?.id ?? "");
      return next;
    });
    setDeletingId(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {channel !== "whatsapp" && <ChannelToggle agent={agent} channel={channel} />}
      <div style={{ display: "flex", gap: 12, minHeight: 420 }}>
        {/* Sidebar de sessões. position:absolute + inset:0 tira este bloco do fluxo
            (senão a altura do conteúdo "puxaria" a linha e quebraria o alinhamento
            com a caixa de texto); a div "relative" ao lado é esticada pelo
            align-items:stretch do flex row até a mesma altura da caixa de texto.
            Dentro, o botão fica fixo no rodapé e o scroll roda só acima dele. */}
        <div style={{ width: 220, flexShrink: 0, position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        <div ref={sectionsListRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 2 }}>
          {sections.map((s, i) => (
            <div
              key={s.id}
              draggable={s.id !== SYSTEM_MESSAGE_ID}
              onDragStart={() => setDragId(s.id)}
              onDragOver={(e) => { e.preventDefault(); if (overId !== s.id) setOverId(s.id); }}
              onDrop={(e) => { e.preventDefault(); if (dragId) reorder(dragId, s.id); setDragId(null); setOverId(null); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              onClick={() => setSelectedId(s.id)}
              // Chip arrastado do editor: pairar aqui abre esta sessão ("chiphover", daí solta
              // dentro dela); soltar direto aqui copia pro fim dela ("chipdrop"). Eventos do ExpressionEditor.
              data-chip-drop={s.id !== selectedId ? "" : undefined}
              ref={(el) => {
                if (!el) return;
                const h = el as HTMLDivElement & { _chipdrop?: (e: Event) => void };
                if (!h._chipdrop) {
                  el.addEventListener("chipdrop", (e) => h._chipdrop?.(e));
                  el.addEventListener("chiphover", () => setSelectedId(s.id));
                }
                h._chipdrop = (e) => setSections((prev) => prev.map((x) => (x.id === s.id
                  ? { ...x, content: (x.content ? x.content.replace(/\s*$/, "") + "\n" : "") + (e as CustomEvent<string>).detail }
                  : x)));
              }}
              style={{
                padding: "10px 12px", borderRadius: 8, cursor: "grab", fontSize: 13,
                color: s.name ? "var(--ink)" : "var(--ink-soft)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                background: s.id === selectedId ? "rgba(195,32,32,0.10)"
                  : s.id === SYSTEM_MESSAGE_ID ? "color-mix(in srgb, var(--surface-hover), white 5%)"
                  : "var(--surface-hover)",
                border: `1px solid ${overId === s.id && dragId && dragId !== s.id ? "var(--brand)" : s.id === selectedId ? "var(--brand)" : "var(--line)"}`,
                opacity: dragId === s.id ? 0.5 : s.paused ? 0.45 : 1,
                flexShrink: 0,
              }}
            >
              {s.name || `Sessão ${i + 1}`}
              {s.id === SYSTEM_MESSAGE_ID && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ marginLeft: 6, verticalAlign: "-1.5px", opacity: 0.7 }}>
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              )}
              {s.paused ? "  · pausado" : (s.starts_at || s.expires_at) && ({ scheduled: "  · agendado", expired: "  · expirado", active: "  · ⏳" })[sectionWindow(s)]}
            </div>
          ))}
        </div>
        <button onClick={addSection} style={{ ...redBtn, padding: "10px 12px", textAlign: "left", flexShrink: 0 }}>+ Nova sessão</button>
        </div>
        </div>

        {/* Editor da sessão selecionada */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          {selected ? (
            <ExpressionEditor
              key={selected.id}
              value={selected.content}
              onChange={(v) => setContent(selected.id, v)}
              minHeight={340}
              highlightTags
              campoKeys={campoKeys}
              campoFields={campoFields}
              insertRef={insertRef}
              headerLeft={
                renamingId === selected.id ? (
                  <input
                    autoFocus
                    value={selected.name}
                    onChange={(e) => rename(selected.id, e.target.value)}
                    onBlur={() => setRenamingId(null)}
                    onKeyDown={(e) => { if (e.key === "Enter") setRenamingId(null); }}
                    placeholder="Nome da sessão"
                    style={{ background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 6, color: "var(--ink)", fontSize: 13, padding: "3px 8px", outline: "none", width: 240 }}
                  />
                ) : (
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--brand)", fontWeight: 600 }}>
                    {(selected.name || "SESSÃO").toUpperCase()}
                  </span>
                )
              }
              headerActions={
                <>
                  {(() => { const w = sectionWindow(selected); return (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, marginRight: 4, color: w === "active" ? "var(--ink-soft)" : "var(--warn)" }}>
                      {w === "expired" ? "expirado" : w === "scheduled" ? "agendado" : "ativo"}
                      <DateRangePicker
                        key={selected.id}
                        title="Janela em que o bloco entra no prompt (início e fim inclusivos, opcionais)"
                        value={{ start: selected.starts_at ?? null, end: selected.expires_at ?? null }}
                        onChange={(r) => setWindow(selected.id, r.start, r.end)}
                        color={w === "active" ? undefined : "var(--warn)"}
                      />
                    </span>
                  ); })()}
                  <button title={selected.paused ? "Despausar — volta a entrar no prompt" : "Pausar — sai do prompt sem apagar"}
                    onClick={() => togglePaused(selected.id)}
                    style={{ ...iconBtn, color: selected.paused ? "var(--warn)" : "var(--ink-soft)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = selected.paused ? "var(--warn)" : "var(--ink-soft)")}>
                    {selected.paused ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                      </svg>
                    )}
                  </button>
                  {selected.id !== SYSTEM_MESSAGE_ID && (
                    <>
                      <button title="Renomear" onClick={() => setRenamingId(selected.id)} style={iconBtn}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink)")} onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-soft)")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        </svg>
                      </button>
                      <button title="Excluir sessão" onClick={() => setDeletingId(selected.id)} style={iconBtn}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--danger)")} onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-soft)")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </>
                  )}
                </>
              }
            />
          ) : (
            <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Crie uma sessão para começar.</div>
          )}

          {isGlobal && selected && selected.id !== SYSTEM_MESSAGE_ID && (
            <div style={{ fontSize: 11, color: "var(--ink-dim)" }}>Campos editáveis: aba Campos. Posição e textos do portal: aba Ordem.</div>
          )}

          {selected?.id === SYSTEM_MESSAGE_ID && (
            <div style={{ fontSize: 12, color: "var(--ink-soft)", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 12px", lineHeight: 1.6 }}>
              Este conteúdo <strong>não entra no prompt</strong>: é enviado à IA a cada chamada como uma mensagem à parte,
              prefixada com “Essa é uma mensagem do sistema, use-a como instrução:”, e não fica registrado no histórico da conversa.
              Aceita {"{{variáveis}}"} — ideal para dados dinâmicos como data/hora e nome do cliente, sem invalidar o cache de prompt.
              Vazio = desligado; pausar também desliga.
            </div>
          )}

          {/* Ações abaixo da caixa de texto, alinhadas à esquerda dela */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => { setImportRaw(""); setImportOpen(true); }} style={ghostBtn}>Importar prompt</button>
            <button onClick={copyPrompt} style={ghostBtn}>
              {copyState === "ok" ? "Copiado!" : copyState === "err" ? "Erro ao copiar" : "Copiar prompt"}
            </button>
            <button onClick={() => save.mutate()} disabled={save.isPending} style={redBtn}>
              {save.isPending ? "Salvando..." : "Salvar"}
            </button>
            <SavedNote show={save.isSuccess} />
          </div>
        </div>
      </div>

      {/* Histórico de alterações */}
      {history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "var(--ink-secondary)", marginBottom: 8 }}>Histórico de alterações</div>
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
            {history.map((h) => (
              <div key={h.id} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--surface-input)" }}>
                <button onClick={() => setSelectedHist(h.id)} style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, textAlign: "left", padding: "10px 0 10px 14px", background: "none", border: "none", color: "var(--ink)", fontSize: 13, cursor: "pointer" }}>
                  <span style={{ flex: 1 }}>{h.changed_by}</span>
                  <span style={{ color: "var(--ink-soft)", fontSize: 12 }}>{fmtDate(h.created_at)}</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, minWidth: 90, textAlign: "right" }}>
                    <span style={{ color: "var(--ok)" }}>+{h.added}</span>{" "}<span style={{ color: "var(--danger)" }}>-{h.removed}</span>
                  </span>
                </button>
                <button title="Restaurar esta versão" onClick={() => { setRestoreStep(1); setRestoring(h); }} style={{ background: "none", border: "none", color: "var(--ink-soft)", fontSize: 16, cursor: "pointer", padding: "8px 14px", lineHeight: 1 }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")} onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-soft)")}>↺</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Importar prompt: colar markdown/HTML */}
      {importOpen && (
        <div {...backdropClose(() => { setImportOpen(false); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setImportOpen(false)} label="Importar prompt" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 640, maxWidth: "92vw", display: "flex", flexDirection: "column", gap: 14 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Importar prompt</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>Cole o prompt completo em markdown (# TÍTULO) ou HTML (&lt;tag&gt;...&lt;/tag&gt;). Sessões aninhadas viram texto dentro da sessão pai.</span>
            <textarea
              autoFocus
              value={importRaw}
              onChange={(e) => setImportRaw(e.target.value)}
              placeholder={"# IDENTIDADE\nVocê é o Luis.\n\n<tom-de-voz>\nSeja direto.\n</tom-de-voz>"}
              style={{ width: "100%", minHeight: 260, background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, fontFamily: "var(--font-mono)", padding: 10, outline: "none", resize: "vertical" }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setImportOpen(false)} style={ghostBtn}>Cancelar</button>
              <button onClick={startImport} disabled={!importRaw.trim()} style={redBtn}>Importar</button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Confirmar importação: substitui as sessões atuais */}
      {importPreview && (
        <div {...backdropClose(() => { setImportPreview(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setImportPreview(null)} role="alertdialog" label="Confirmar importação" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 440, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Confirmar importação</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>
              O prompt atual será <strong>substituído</strong> pelas {importPreview.length} sessão(ões) importada(s): {importPreview.map((s) => s.name || "Sessão").join(", ")}. Essa alteração entra no histórico e pode ser revertida por lá. Confirmar?
            </span>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setImportPreview(null)} style={ghostBtn} disabled={importMut.isPending}>Cancelar</button>
              <button onClick={confirmImport} disabled={importMut.isPending} style={redBtn}>{importMut.isPending ? "Importando..." : "Confirmar"}</button>
            </div>
          </DialogPanel>
        </div>
      )}

      {selectedHist && <DiffModal agentId={agent.id} histId={selectedHist} channel={channel} onClose={() => setSelectedHist(null)} />}

      {/* Confirmar exclusão de sessão */}
      {deletingId && (
        <div {...backdropClose(() => { setDeletingId(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setDeletingId(null)} role="alertdialog" label="Excluir sessão" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 420, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Excluir sessão</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>Tem certeza? Esta ação não pode ser desfeita.</span>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeletingId(null)} style={ghostBtn}>Cancelar</button>
              <button onClick={() => remove(deletingId)} style={redBtn}>Excluir</button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Restaurar versão (2 confirmações) */}
      {restoring && (
        <div {...backdropClose(() => { setRestoring(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setRestoring(null)} role="alertdialog" label="Restaurar versão" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 440, display: "flex", flexDirection: "column", gap: 16 }}>
            {restoreStep === 1 ? (
              <>
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Restaurar versão</span>
                <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>Deseja restaurar o prompt para a versão de <strong>{restoring.changed_by}</strong> ({fmtDate(restoring.created_at)})?</span>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setRestoring(null)} style={ghostBtn}>Não</button>
                  <button onClick={() => setRestoreStep(2)} style={redBtn}>Sim</button>
                </div>
              </>
            ) : (
              <>
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Confirmar restauração</span>
                <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>Isso vai <strong>reverter o prompt atual</strong> para a versão selecionada e registrar a alteração no histórico. Confirmar?</span>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => setRestoring(null)} style={ghostBtn}>Cancelar</button>
                  <button onClick={() => restoreMut.mutate(restoring.id)} disabled={restoreMut.isPending} style={redBtn}>{restoreMut.isPending ? "Restaurando..." : "Restaurar"}</button>
                </div>
              </>
            )}
          </DialogPanel>
        </div>
      )}
    </div>
  );
}
